import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Coffee, User, LogOut, Settings, Home, Users, Newspaper, Mail } from 'lucide-react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.98) 0%, rgba(26, 26, 26, 0.95) 50%, rgba(0, 0, 0, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e0c16f;
  z-index: 1000;
  padding: 12px 0; /* Malo smanjen padding */
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px; /* Smanjen padding za ivice */
  box-sizing: border-box;
  width: 100%;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Logo nikad ne sme da se smanji */
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px; /* Smanjen font za mobilni */
  font-weight: 700;
  font-family: var(--font-heading);
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  
  svg {
    width: 28px;
    height: 28px;
    color: #e0c16f;
  }

  @media (max-width: 480px) {
    span { display: none; } /* Ostavljamo samo ikonicu kafe na baš malim ekranima */
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 992px) {
    display: none; /* Sakrivamo navigaciju na tabletima i mobilnim */
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #cccccc;
  text-decoration: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  padding: 8px 12px;
  transition: all 0.2s ease;
  
  &:hover { color: #e0c16f; }
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* Smanjen gap */
`;

const HeaderIconBtn = styled.button`
  background: none;
  border: none;
  color: #cccccc;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover { color: #e0c16f; }
`;

const AuthButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: 2px solid #333333;
  border-radius: 8px;
  background: transparent;
  color: #ffffff;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &.dashboard {
    background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
    color: #000000;
    border-color: #e0c16f;
  }

  @media (max-width: 768px) {
    padding: 8px;
    span { display: none; } /* Sakriva tekst "Dashboard" na mobilnom */
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background: #111111;
  border-radius: 8px;
  border: 1px solid #333333;

  @media (max-width: 768px) {
    display: none; /* Sakriva ime i ulogu na mobilnom da oslobodi prostor */
  }
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  background: var(--primary-gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
`;

const LogoutButton = styled.button`
  padding: 8px;
  background: transparent;
  color: #ff4757; /* Crvena boja za logout */
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: rgba(255, 71, 87, 0.1);
    border-color: #ff4757;
  }

  @media (max-width: 768px) {
    span { display: none; } /* Sakriva tekst "Logout" */
  }
`;

const Header = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    if(window.confirm("Logout?")) {
      logout();
      navigate('/');
    }
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoSection>
          <Logo to="/">
            <Coffee />
            <span>BUCX</span>
          </Logo>
        </LogoSection>

        <Navigation>
          <NavLink to="/"><Home size={16} /> Home</NavLink>
          {isAuthenticated() && <NavLink to="/feed"><Newspaper size={16} /> Feed</NavLink>}
          <NavLink to="/explore-creators"><Users size={16} /> Explore</NavLink>
        </Navigation>

        <AuthSection>
          {isAuthenticated() ? (
            <>
              <HeaderIconBtn onClick={() => navigate('/messages/list')} title="Inbox">
                <Mail size={22} />
              </HeaderIconBtn>

              <UserInfo>
                <UserAvatar><User size={14} /></UserAvatar>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                  <span style={{color: '#fff', fontSize: '0.75rem', fontWeight: 'bold'}}>{user.username}</span>
                </div>
              </UserInfo>
              
              <AuthButton to="/dashboard" className="dashboard" title="Dashboard">
                <Settings size={18} />
                <span>Dashboard</span>
              </AuthButton>
              
              <LogoutButton onClick={handleLogout} title="Logout">
                <LogOut size={18} />
                <span>Logout</span>
              </LogoutButton>
            </>
          ) : (
            <>
              <AuthButton to="/login">Login</AuthButton>
              <AuthButton to="/signup" className="dashboard">Signup</AuthButton>
            </>
          )}
        </AuthSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;